<template>
    <UButtonGroup :size="size" orientation="horizontal" class="w-full">
        <UButton
            color="white"
            trailing-icon="i-heroicons-chevron-down-20-solid"
            :disabled="disabled"
            class="shrink-0"
            @click="pickingCountry = true"
        >
            <UIcon :name="flagIcon(country)" dynamic class="size-4 shrink-0" />
            <span class="tabular-nums">+{{ dialCode }}</span>
        </UButton>
        <UInput
            v-model="national"
            type="tel"
            inputmode="tel"
            autocomplete="tel-national"
            :placeholder="example"
            :disabled="disabled"
            class="flex-1"
            @update:model-value="onNationalInput"
            @blur="emit('change')"
        />
    </UButtonGroup>
    <!-- A separate dialog because card bodies are overflow-auto and would clip an inline dropdown inside modals. -->
    <UModal v-model="pickingCountry" :ui="{ width: 'sm:max-w-sm' }">
        <UCommandPalette
            :model-value="selectedCountry"
            :groups="[{ key: 'countries', commands: countries }]"
            :autoselect="false"
            :ui="{ container: 'relative max-h-96 overflow-y-auto divide-y divide-gray-100 scroll-py-2' }"
            :close-button="null"
            placeholder="Search country or code..."
            :fuse="{ fuseOptions: { keys: ['label', 'code', 'suffix'], threshold: 0.2, ignoreLocation: true }, resultLimit: 300 }"
            @update:model-value="onCountryPicked"
        >
            <template #countries-icon="{ command }">
                <UIcon :name="flagIcon(command.code)" dynamic class="size-4 shrink-0" />
            </template>
        </UCommandPalette>
    </UModal>
</template>

<script setup lang="ts">
import { type CountryCode, getCountries, getCountryCallingCode, getExampleNumber, parsePhoneNumberFromString } from 'libphonenumber-js/max'
import examples from 'libphonenumber-js/mobile/examples'
import { DEFAULT_PHONE_COUNTRY, buildE164, parseStoredPhone } from '~/utils/phone'

const props = withDefaults(defineProps<{
    modelValue?: string | null
    size?: 'sm' | 'md' | 'lg' | 'xl'
    disabled?: boolean
}>(), {
    modelValue: '',
    size: 'lg',
    disabled: false,
})

const emit = defineEmits<{ 'update:modelValue': [value: string]; change: [] }>()

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
const countries = getCountries()
    .map(code => ({ id: code, code, label: regionNames.of(code) ?? code, suffix: '+' + getCountryCallingCode(code) }))
    .sort((a, b) => a.label.localeCompare(b.label))

const country = ref<CountryCode>(DEFAULT_PHONE_COUNTRY)
const national = ref('')
const pickingCountry = ref(false)
let lastEmitted: string | undefined

const selectedCountry = computed(() => countries.find(c => c.code === country.value))
const dialCode = computed(() => getCountryCallingCode(country.value))
const example = computed(() => getExampleNumber(country.value, examples)?.formatNational() ?? '')

function flagIcon(code: string) {
    return `i-circle-flags-${code.toLowerCase()}`
}

watch(() => props.modelValue, (value) => {
    if (value === lastEmitted) return
    const stored = parseStoredPhone(value)
    country.value = stored?.country ?? DEFAULT_PHONE_COUNTRY
    national.value = stored?.national ?? ''
}, { immediate: true })

function onNationalInput(value: string) {
    const compact = value.replace(/[^\d+]/g, '')
    if (compact.startsWith('+') || compact.startsWith('00')) {
        const pasted = parsePhoneNumberFromString(compact.startsWith('00') ? '+' + compact.slice(2) : compact)
        if (pasted?.country) {
            country.value = pasted.country
            national.value = pasted.nationalNumber
        }
    }
    emitValue()
}

function onCountryPicked(picked?: { code: CountryCode }) {
    pickingCountry.value = false
    if (!picked) return
    country.value = picked.code
    emitValue()
    emit('change')
}

function emitValue() {
    lastEmitted = buildE164(national.value, country.value)
    emit('update:modelValue', lastEmitted)
}
</script>
